import { useState, useEffect, useCallback, useRef } from 'react';
import type { FilterableTableHandle } from '../../components/ui/FilterableTable';
import { useBulkSelectionScope } from '../../hooks/useBulkSelectionScope';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DocumentUpload } from '../../components/DocumentUpload';
import { DocumentList } from '../../components/DocumentList';
import { DocumentsStatsCards, type CardClickPayload } from '../../components/documents/DocumentsStatsCards';
import {
  getDocuments,
  getDocumentDistinctTags,
  getDocumentFilterFacets,
  updateDocumentTags,
  getDocumentsStats,
  retryDocument,
  type DocumentsStats,
  type DocumentFilterFacets,
  type SortField,
  type SortOrder,
} from '../../services/api';
import { extractionService } from '../../services/extractionService';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { downloadBulkCsv, downloadBulkJson } from '../../services/exportService';
import { tenantMeService } from '../../services/tenantMeService';
import { usePolling } from '../../contexts/PollingContext';
import { useAuth } from '../../contexts/AuthContext';
import { PageContainer } from '../../components/layout';
import { useExtractionQueuePause } from '../../hooks/useExtractionQueuePause';
import type { Document } from '../../types/document';

export function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { isPollingEnabled } = usePolling();
  const { user } = useAuth();
  const documentsRef = useRef<Document[]>([]);

  // URL-driven filters (all filters persist via the query string,
  // so refreshing or sharing the URL preserves the analyst's view).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStatus = searchParams.get('status') || '';
  const urlDomain = searchParams.get('domain') || '';
  const urlTags = searchParams.getAll('tags');
  const urlSchemaVersions = searchParams.getAll('schema_version');
  const urlPromptVersions = searchParams.getAll('prompt_version');
  const urlLlmVersions = searchParams.getAll('llm_version');
  const urlOcrVersions = searchParams.getAll('ocr_version');
  const urlFilenameSearch = searchParams.get('filename_search') || '';
  const urlPeople = searchParams.getAll('people');
  // PROD1.7.8 N2 — multi-value any-match sur les flags LLM
  const urlFlags = searchParams.getAll('flags');
  const urlConfidenceLevels = searchParams.getAll('confidence_level');

  // Pagination state (#496) — driven by query string for shareable URLs.
  const PAGE_SIZE_OPTIONS = [25, 50, 100, 250] as const;
  const DEFAULT_PAGE_SIZE = 25;
  const parsedPage = parseInt(searchParams.get('page') || '', 10);
  const parsedPageSize = parseInt(searchParams.get('page_size') || '', 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const pageSize = (PAGE_SIZE_OPTIONS as readonly number[]).includes(parsedPageSize)
    ? parsedPageSize
    : DEFAULT_PAGE_SIZE;
  // Default sort = last_action_at desc, to match the FilterableTable's
  // `defaultSort={{ key: 'dates::last_action', direction: 'desc' }}` in DocumentList.
  const sortBy = (searchParams.get('sort_by') as SortField | null) || 'last_action_at';
  const sortOrder = (searchParams.get('sort_order') as SortOrder | null) || 'desc';
  const [totalDocuments, setTotalDocuments] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalDocuments / pageSize));

  // Map FilterableTable column key → backend SortField, and the reverse
  // direction for the visible sort indicator. The Dates column exposes 4
  // sub-options ; the backend supports each one via dedicated SortField
  // values (LEFT JOIN current extraction).
  const DATES_OPTION_TO_SORTBY: Record<string, SortField> = {
    last_action: 'last_action_at',
    upload: 'created_at',
    extracted: 'extracted_at',
    validated: 'validated_at',
  };
  const DATES_SORTBY_TO_OPTION: Record<string, string> = {
    last_action_at: 'last_action',
    created_at: 'upload',
    extracted_at: 'extracted',
    validated_at: 'validated',
  };

  const mapSortKeyToServer = (key: string | null): SortField => {
    if (!key) return 'last_action_at';
    if (key.startsWith('dates::')) {
      const opt = key.split('::')[1];
      return DATES_OPTION_TO_SORTBY[opt] ?? 'created_at';
    }
    const [base] = key.split('::');
    const allowed: SortField[] = [
      'filename', 'domain', 'status', 'created_at',
      'extracted_at', 'validated_at', 'last_action_at',
      'schema_version', 'prompt_version', 'llm_version', 'ocr_version',
    ];
    return (allowed as string[]).includes(base) ? (base as SortField) : 'created_at';
  };

  // Reverse map for the FilterableTable sort indicator. The 4 date-related
  // SortField values reflect back into the "dates::*" composite key so the
  // dropdown highlights the right sub-option in blue. Idem pour les 4 axes
  // de la colonne 'configs' (S/P/L/O fusionnés en cellule 2x2).
  const CONFIGS_SUB_KEYS = new Set([
    'schema_version', 'prompt_version', 'llm_version', 'ocr_version',
  ]);
  const controlledSort = (() => {
    const datesOpt = DATES_SORTBY_TO_OPTION[sortBy];
    if (datesOpt) {
      return { key: `dates::${datesOpt}`, direction: sortOrder } as const;
    }
    if (CONFIGS_SUB_KEYS.has(sortBy)) {
      return { key: `configs::${sortBy}`, direction: sortOrder } as const;
    }
    return { key: sortBy, direction: sortOrder } as const;
  })();

  const updateSearchParams = useCallback(
    (mutator: (p: URLSearchParams) => void, opts: { replace?: boolean } = {}) => {
      const next = new URLSearchParams(searchParams);
      mutator(next);
      setSearchParams(next, { replace: opts.replace ?? false });
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      updateSearchParams((p) => {
        if (nextPage <= 1) p.delete('page');
        else p.set('page', String(nextPage));
      });
    },
    [updateSearchParams],
  );

  const setPageSize = useCallback(
    (nextSize: number) => {
      updateSearchParams((p) => {
        p.delete('page');
        if (nextSize === DEFAULT_PAGE_SIZE) p.delete('page_size');
        else p.set('page_size', String(nextSize));
      });
    },
    [updateSearchParams],
  );

  const handleServerSortChange = useCallback(
    (key: string | null, direction: 'asc' | 'desc' | null) => {
      const nextField = mapSortKeyToServer(key);
      const nextOrder: SortOrder = direction ?? 'desc';
      updateSearchParams((p) => {
        p.delete('page');
        // Default sort (last_action_at desc) → keep the URL clean.
        if (nextField === 'last_action_at' && nextOrder === 'desc') {
          p.delete('sort_by');
          p.delete('sort_order');
        } else {
          p.set('sort_by', nextField);
          p.set('sort_order', nextOrder);
        }
      });
    },
    [updateSearchParams],
  );

  // Reset to page 1 when ANY filter changes.
  const filterSig = [
    urlStatus,
    urlDomain,
    urlTags.join(','),
    urlSchemaVersions.join(','),
    urlPromptVersions.join(','),
    urlLlmVersions.join(','),
    urlOcrVersions.join(','),
    urlFilenameSearch,
    urlPeople.join(','),
  ].join('|');
  const lastFilterSigRef = useRef<string>(filterSig);
  useEffect(() => {
    if (filterSig !== lastFilterSigRef.current) {
      lastFilterSigRef.current = filterSig;
      if (page !== 1) {
        updateSearchParams(
          (p) => {
            p.delete('page');
          },
          { replace: true },
        );
      }
    }
  }, [filterSig, page, updateSearchParams]);

  // --- Filter facets (full-dataset distinct values for dropdowns) ---
  // Declared before controlledFilters/controlledSearches because the
  // people-search resolution depends on whether the URL value matches a
  // known facet.
  const [filterFacets, setFilterFacets] = useState<DocumentFilterFacets | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    getDocumentFilterFacets()
      .then((f) => {
        if (!cancelled) setFilterFacets(f);
      })
      .catch((e) => console.error('Erreur chargement filter facets:', e));
    return () => {
      cancelled = true;
    };
  }, []);

  // --- Controlled filters/searches for FilterableTable (server-side) ---

  // Column key → backend URL param multi-value mapping. Keep one source of
  // truth so handler + controlledFilters agree. `people` is unified: the
  // checklist + the search input both write to the same `people` query
  // param (each entry = a substring ilike, OR across entries server-side).
  const FILTER_COL_TO_PARAM: Record<string, string> = {
    status: 'status',
    domain: 'domain',
    tags: 'tags',
    flags: 'flags',
    schema_version: 'schema_version',
    prompt_version: 'prompt_version',
    llm_version: 'llm_version',
    ocr_version: 'ocr_version',
    people: 'people',
    confidence_score: 'confidence_level',
  };

  const SEARCH_COL_TO_PARAM: Record<string, string> = {
    filename: 'filename_search',
    // `people` search shares the `people` multi-value param with the checklist.
    people: 'people',
  };

  // status & domain in the URL are stored as CSV (legacy/single-value
  // compatibility) ; tags + version filters + people are stored as
  // multi-value (?tags=a&tags=b). We expose both as string[] in controlledFilters.
  const controlledFilters: Record<string, string[]> = {
    status: urlStatus ? urlStatus.split(',').filter(Boolean) : [],
    domain: urlDomain ? urlDomain.split(',').filter(Boolean) : [],
    tags: urlTags,
    flags: urlFlags,
    schema_version: urlSchemaVersions,
    prompt_version: urlPromptVersions,
    llm_version: urlLlmVersions,
    ocr_version: urlOcrVersions,
    people: urlPeople,
    confidence_score: urlConfidenceLevels,
  };

  // Search inputs. For `people`, when a single value is stored AND it is not
  // exactly one of the discovered facets (i.e. a free-text substring), expose
  // it as the search text. Otherwise keep the search empty (the checklist
  // already reflects the state).
  const controlledSearches: Record<string, string> = {
    filename: urlFilenameSearch,
    people: urlPeople.length === 1 && !filterFacets?.people?.includes(urlPeople[0])
      ? urlPeople[0]
      : '',
  };

  const handleFiltersChange = useCallback(
    (colKey: string, selected: string[]) => {
      const param = FILTER_COL_TO_PARAM[colKey];
      if (!param) return;
      updateSearchParams((p) => {
        p.delete(param);
        if (param === 'status' || param === 'domain') {
          if (selected.length > 0) p.set(param, selected.join(','));
        } else {
          for (const v of selected) p.append(param, v);
        }
        p.delete('page');
      });
    },
    [updateSearchParams],
  );

  const handleSearchChange = useCallback(
    (colKey: string, text: string) => {
      const param = SEARCH_COL_TO_PARAM[colKey];
      if (!param) return;
      updateSearchParams((p) => {
        const trimmed = text.trim();
        // `people` is a multi-value param shared with the checklist : a
        // search update replaces ALL values with the single search needle.
        // Clearing the search empties the param entirely.
        p.delete(param);
        if (trimmed) {
          if (param === 'people') p.append(param, trimmed);
          else p.set(param, trimmed);
        }
        p.delete('page');
      });
    },
    [updateSearchParams],
  );

  // Default filter for analystes only: status=extracted at first mount.
  // admin_client/superadmin keep the full overview (no default filter).
  // Persisted across navigations: if the user explicitly clears it, we won't
  // re-apply it when they re-open the page or return from a document detail.
  // Tracked in sessionStorage to scope to the current browser tab session.
  const DEFAULT_FILTER_FLAG = 'dokimo.docs.defaultStatusApplied';
  const defaultFilterAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultFilterAppliedRef.current) return;
    if (!user) return;
    if (sessionStorage.getItem(DEFAULT_FILTER_FLAG) === '1') {
      defaultFilterAppliedRef.current = true;
      return;
    }
    if (searchParams.get('status') !== null) {
      defaultFilterAppliedRef.current = true;
      sessionStorage.setItem(DEFAULT_FILTER_FLAG, '1');
      return;
    }
    if (user.role_in_active_client === 'analyste') {
      setSearchParams({ status: 'extracted' }, { replace: true });
    }
    defaultFilterAppliedRef.current = true;
    sessionStorage.setItem(DEFAULT_FILTER_FLAG, '1');
  }, [user, searchParams, setSearchParams]);

  // Tenant extraction policy — drives the "Lancer extraction" button (#447).
  const [extractionPolicy, setExtractionPolicy] = useState<string | undefined>(undefined);
  useEffect(() => {
    // tenant/me exige admin_client/superadmin — inutile pour les analystes,
    // qui ne voient de toute façon pas le bouton "Lancer extraction".
    const role = user?.role_in_active_client;
    if (role !== 'admin_client' && user?.system_role !== 'superadmin') return;
    tenantMeService
      .getMe()
      .then((me) => setExtractionPolicy(me.extraction_policy))
      .catch((e) => console.error('Erreur chargement tenant.me:', e));
  }, [user]);

  // Mini-dashboard stats
  const [stats, setStats] = useState<DocumentsStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const loadStats = useCallback(async () => {
    try {
      const s = await getDocumentsStats();
      setStats(s);
    } catch (e) {
      console.error('Erreur chargement stats:', e);
    } finally {
      setStatsLoading(false);
    }
  }, []);
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleCardClick = useCallback(
    (payload: CardClickPayload) => {
      const next = new URLSearchParams(searchParams);
      next.delete('validated_within');
      if (payload.statusCsv === null) {
        next.delete('status');
      } else {
        next.set('status', payload.statusCsv);
        if (payload.extraParams) {
          for (const [k, v] of Object.entries(payload.extraParams)) {
            next.set(k, v);
          }
        }
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Selection state for bulk actions
  const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());
  const hasSelection = selectedKeys.size > 0;

  // Bulk tag dropdown
  const [showBulkTagDropdown, setShowBulkTagDropdown] = useState<'add' | 'remove' | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState('');
  const tagDropdownRef = useRef<HTMLDivElement>(null);

  // Table ref & counts
  const tableRef = useRef<FilterableTableHandle>(null);
  const [filteredCount, setFilteredCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const handleFilteredCountChange = useCallback((filtered: number, total: number) => {
    setFilteredCount(filtered);
    setTotalCount(total);
  }, []);

  // Garde bulk : la sélection persiste entre pages/filtres (server-side), donc
  // des docs sélectionnés peuvent être HORS de la vue courante chargée. Le hook
  // propose « Tous / Visibles seulement » avant d'appliquer. Utilisé pour export
  // et tags (delete a son propre modal, enrichi ci-dessous).
  const { requestBulk, modal: bulkScopeModal } = useBulkSelectionScope(selectedKeys, tableRef);
  // Nb de docs sélectionnés absents de la vue courante (pour message + garde tags).
  const hiddenSelectedCount = Array.from(selectedKeys).filter(
    (k) => !documents.some((d) => d.id === k)
  ).length;
  // Server-side filters now → "active filter" means any URL filter param is set.
  // We keep the legacy client-side check (filteredCount < totalCount) as a
  // fallback for any consumer that doesn't pass server controls.
  const hasActiveServerFilters =
    !!urlStatus || !!urlDomain || urlTags.length > 0 ||
    urlSchemaVersions.length > 0 || urlPromptVersions.length > 0 ||
    urlLlmVersions.length > 0 || urlOcrVersions.length > 0 ||
    !!urlFilenameSearch || urlPeople.length > 0 ||
    urlConfidenceLevels.length > 0;
  const hasActiveTableFilters = hasActiveServerFilters || filteredCount < totalCount;

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReextractConfirm, setShowReextractConfirm] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Keep documents ref in sync
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  const loadDocuments = useCallback(async (forceUpdate = false) => {
    try {
      const response = await getDocuments({
        page,
        pageSize,
        status: urlStatus || undefined,
        domain: urlDomain || undefined,
        tags: urlTags.length > 0 ? urlTags : undefined,
        filenameSearch: urlFilenameSearch || undefined,
        people: urlPeople.length > 0 ? urlPeople : undefined,
        schemaVersion: urlSchemaVersions.length > 0 ? urlSchemaVersions : undefined,
        promptVersion: urlPromptVersions.length > 0 ? urlPromptVersions : undefined,
        llmVersion: urlLlmVersions.length > 0 ? urlLlmVersions : undefined,
        ocrVersion: urlOcrVersions.length > 0 ? urlOcrVersions : undefined,
        flags: urlFlags.length > 0 ? urlFlags : undefined,
        confidenceLevel: urlConfidenceLevels.length > 0 ? urlConfidenceLevels : undefined,
        sortBy,
        sortOrder,
      });
      setTotalDocuments(response.total);
      if (forceUpdate) {
        setDocuments(response.items);
      } else {
        setDocuments(prev => {
          const newItems = response.items;
          if (prev.length !== newItems.length || (prev[0]?.id !== newItems[0]?.id)) {
            return newItems;
          }
          const hasChanges = newItems.some((newDoc, idx) => prev[idx]?.status !== newDoc.status);
          return hasChanges ? newItems : prev;
        });
      }
    } catch (e: any) {
      console.error('Erreur chargement documents:', e);
    } finally {
      setLoading(false);
    }
  }, [
    urlStatus, urlDomain, page, pageSize, sortBy, sortOrder,
    urlTags.join(','), urlFilenameSearch, urlPeople.join(','),
    urlSchemaVersions.join(','), urlPromptVersions.join(','),
    urlLlmVersions.join(','), urlOcrVersions.join(','),
    urlFlags.join(','), urlConfidenceLevels.join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const loadTags = useCallback(async () => {
    try {
      const tags = await getDocumentDistinctTags();
      setAllTags(tags);
    } catch (e) {
      console.error('Erreur chargement tags:', e);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
    loadTags();
  }, [loadDocuments, loadTags]);

  // Auto-refresh polling for processing documents
  useEffect(() => {
    if (!isPollingEnabled || location.pathname !== '/extraction/documents') return;

    const interval = setInterval(() => {
      const hasProcessing = documentsRef.current.some(
        (doc) => doc.status === 'processing' || doc.status === 'uploaded'
      );
      if (hasProcessing && isPollingEnabled) {
        loadDocuments();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [location.pathname, isPollingEnabled, loadDocuments]);

  // Close tag dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setShowBulkTagDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showBulkTagDropdown]);

  const handleUploadSuccess = useCallback(() => {
    loadDocuments(true);
  }, [loadDocuments]);

  const handleActionComplete = useCallback(() => {
    loadDocuments(true);
    loadStats();
  }, [loadDocuments, loadStats]);

  const handleResetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleSelectDocument = useCallback((doc: Document) => {
    navigate(`/extraction/documents/${doc.id}`);
  }, [navigate]);

  // ── Bulk actions ──

  // Sous-ensembles éligibles parmi la sélection courante (computed à partir
  // de `selectedDocs` déclaré plus bas — voir bloc isMultiDomain)
  const extractableDocs = documents.filter(
    (d) => selectedKeys.has(d.id) && (d.status === 'uploaded' || d.status === 'error')
  );
  const reextractableDocs = documents.filter(
    (d) => selectedKeys.has(d.id) && (d.status === 'extracted' || d.status === 'validated')
  );

  const handleBulkExtract = useCallback(async () => {
    if (extractableDocs.length === 0) return;
    const results = await Promise.allSettled(
      extractableDocs.map((doc) => extractionService.retryExtraction(doc.id))
    );
    const success = results.filter((r) => r.status === 'fulfilled').length;
    toast.success(`${success}/${extractableDocs.length} extraction(s) lancée(s)`);
    setSelectedKeys(new Set());
    loadDocuments(true);
  }, [extractableDocs, loadDocuments]);

  const handleBulkReextract = useCallback(async () => {
    if (reextractableDocs.length === 0) return;
    setShowReextractConfirm(false);
    setBulkActionLoading(true);
    const results = await Promise.allSettled(
      reextractableDocs.map((doc) => retryDocument(doc.id))
    );
    const success = results.filter((r) => r.status === 'fulfilled').length;
    toast.success(`${success}/${reextractableDocs.length} ré-extraction(s) lancée(s)`);
    setSelectedKeys(new Set());
    setBulkActionLoading(false);
    loadDocuments(true);
  }, [reextractableDocs, loadDocuments]);

  const handleBulkDelete = useCallback(async () => {
    setBulkActionLoading(true);
    const results = await Promise.allSettled(
      Array.from(selectedKeys).map((id) => extractionService.deleteDocument(String(id)))
    );
    const success = results.filter((r) => r.status === 'fulfilled').length;
    toast.success(`${success}/${selectedKeys.size} document(s) supprimé(s)`);
    setSelectedKeys(new Set());
    setShowDeleteConfirm(false);
    setBulkActionLoading(false);
    loadDocuments(true);
  }, [selectedKeys, loadDocuments]);

  const handleBulkAddTag = useCallback(async (keys: Array<string | number>, tag: string) => {
    if (!tag.trim()) return;
    setBulkActionLoading(true);
    let success = 0;
    let skippedOffView = 0;
    for (const id of keys) {
      const docId = String(id);
      const doc = documents.find((d) => d.id === docId);
      // Server-side : on ne connaît les tags QUE des docs de la vue courante.
      // Un doc hors vue est ignoré (on ne peut pas lire/fusionner ses tags).
      if (!doc) { skippedOffView++; continue; }
      const currentTags = doc.tags || [];
      if (currentTags.includes(tag)) continue;
      try {
        await updateDocumentTags(docId, [...currentTags, tag]);
        success++;
      } catch (e) {
        console.error(`Add tag failed for ${id}:`, e);
      }
    }
    toast.success(`Tag "${tag}" ajouté à ${success} document(s)`);
    if (skippedOffView > 0) {
      toast(`${skippedOffView} doc(s) hors de la vue ignoré(s) (tags modifiables page par page)`, { icon: 'ℹ️' });
    }
    setShowBulkTagDropdown(null);
    setNewTagInput('');
    setBulkActionLoading(false);
    loadDocuments(true);
    loadTags();
  }, [documents, loadDocuments, loadTags]);

  const handleBulkRemoveTag = useCallback(async (keys: Array<string | number>, tag: string) => {
    setBulkActionLoading(true);
    let success = 0;
    let skippedOffView = 0;
    for (const id of keys) {
      const docId = String(id);
      const doc = documents.find((d) => d.id === docId);
      if (!doc) { skippedOffView++; continue; }
      const currentTags = doc.tags || [];
      if (!currentTags.includes(tag)) continue;
      try {
        await updateDocumentTags(docId, currentTags.filter((t) => t !== tag));
        success++;
      } catch (e) {
        console.error(`Remove tag failed for ${id}:`, e);
      }
    }
    toast.success(`Tag "${tag}" retiré de ${success} document(s)`);
    if (skippedOffView > 0) {
      toast(`${skippedOffView} doc(s) hors de la vue ignoré(s) (tags modifiables page par page)`, { icon: 'ℹ️' });
    }
    setShowBulkTagDropdown(null);
    setBulkActionLoading(false);
    loadDocuments(true);
    loadTags();
  }, [documents, loadDocuments, loadTags]);

  // Check if selected documents span multiple domains (block export if so)
  const selectedDocs = documents.filter((d) => selectedKeys.has(d.id));
  const selectedDomains = new Set(selectedDocs.map((d) => d.domain));
  const isMultiDomain = selectedDomains.size > 1;

  const handleBulkExportCsv = useCallback(async (keys: Array<string | number>) => {
    const ids = keys.map(String);
    try {
      await downloadBulkCsv(ids);
      toast.success('Export CSV téléchargé');
    } catch (e) {
      toast.error('Erreur lors de l\'export CSV');
    }
  }, []);

  const handleBulkExportJson = useCallback(async (keys: Array<string | number>) => {
    const ids = keys.map(String);
    try {
      await downloadBulkJson(ids);
      toast.success('Export JSON téléchargé');
    } catch (e) {
      toast.error('Erreur lors de l\'export JSON');
    }
  }, []);

  // Get tags present on selected documents (for remove dropdown)
  const selectedDocsTags = Array.from(
    new Set(
      documents
        .filter((d) => selectedKeys.has(d.id))
        .flatMap((d) => d.tags || [])
    )
  ).sort();

  const extractionQueuePaused = useExtractionQueuePause();

  return (
    <PageContainer>
      {extractionQueuePaused && (
        <div
          role="alert"
          className="mb-3 px-4 py-2 rounded border border-yellow-300 bg-yellow-50 text-sm text-yellow-900"
        >
          <span className="font-medium">⏸ Extractions temporairement en pause.</span>{' '}
          <span className="text-xs text-yellow-700">
            Les actions "Extraire" et "Ré-extraire" sont indisponibles. Les documents
            uploadés en attendant seront traités à la reprise.
          </span>
        </div>
      )}
      {/* Upload UI masqué si le tenant a désactivé le mode d'ingestion ui_upload.
          Fallback à true si le flag est absent (défaut BD = ui_upload activé) —
          on ne cache pas le bouton par défaut. NB : masquage UX seulement, l'API
          d'upload n'est pas gardée (choix assumé). */}
      {(user?.active_client?.ingestion_modes_enabled?.ui_upload ?? true) && (
        <DocumentUpload onUploadSuccess={handleUploadSuccess} />
      )}

      {/* Mini-dashboard inbox cards */}
      <DocumentsStatsCards
        stats={stats}
        loading={statsLoading}
        activeStatusCsv={urlStatus || null}
        onCardClick={handleCardClick}
      />

      {/* URL filter info banner */}
      {(urlStatus || urlDomain) && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-sm flex items-center justify-between">
          <span className="text-blue-800">
            Filtre actif :
            {urlStatus && <span className="ml-2 font-medium">statut = {urlStatus}</span>}
            {urlDomain && <span className="ml-2 font-medium">domaine = {urlDomain}</span>}
          </span>
          <button
            onClick={handleResetFilters}
            className="text-blue-600 hover:underline text-xs"
          >
            Effacer filtres URL
          </button>
        </div>
      )}

      {/* Toolbar: filter info (left) + bulk actions (right) */}
      <div className="mb-3 p-3 rounded-lg border bg-gray-50 border-gray-200 flex items-center justify-between flex-wrap gap-2">
        {/* Left: document counts & filter info */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>{totalDocuments} document(s)</span>
          {hasActiveTableFilters && (
            <>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => tableRef.current?.resetFilters()}
                className="text-blue-600 hover:underline"
              >
                Effacer filtres
              </button>
            </>
          )}
        </div>

        {/* Right: selection count + bulk actions */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`text-sm font-medium ${hasSelection ? 'text-blue-800' : 'text-gray-400'}`}>
            {hasSelection ? `${selectedKeys.size} sélectionné(s) :` : 'Aucune sélection'}
          </span>

          {/* + Tag */}
          <div className="relative" ref={showBulkTagDropdown === 'add' ? tagDropdownRef : undefined}>
            <button
              onClick={() => setShowBulkTagDropdown(showBulkTagDropdown === 'add' ? null : 'add')}
              disabled={!hasSelection || bulkActionLoading}
              className="px-3 py-1.5 text-xs font-medium rounded bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              + Tag
            </button>
            {showBulkTagDropdown === 'add' && (
              <div className="absolute z-50 mt-1 bg-white border rounded-lg shadow-lg p-2 w-56">
                <div className="flex gap-1 mb-2">
                  <input
                    type="text"
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { const t = newTagInput; requestBulk((keys) => handleBulkAddTag(keys, t)); } }}
                    placeholder="Nouveau tag..."
                    className="flex-1 px-2 py-1 text-xs border rounded"
                    autoFocus
                  />
                  <button
                    onClick={() => { const t = newTagInput; requestBulk((keys) => handleBulkAddTag(keys, t)); }}
                    className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    OK
                  </button>
                </div>
                {allTags.length > 0 && (
                  <div className="max-h-32 overflow-y-auto">
                    {allTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => requestBulk((keys) => handleBulkAddTag(keys, tag))}
                        className="block w-full text-left px-2 py-1 text-xs hover:bg-gray-100 rounded"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* - Tag */}
          <div className="relative" ref={showBulkTagDropdown === 'remove' ? tagDropdownRef : undefined}>
            <button
              onClick={() => setShowBulkTagDropdown(showBulkTagDropdown === 'remove' ? null : 'remove')}
              disabled={!hasSelection || bulkActionLoading}
              className="px-3 py-1.5 text-xs font-medium rounded bg-orange-100 text-orange-800 hover:bg-orange-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              - Tag
            </button>
            {showBulkTagDropdown === 'remove' && (
              <div className="absolute z-50 mt-1 bg-white border rounded-lg shadow-lg p-2 w-56">
                {selectedDocsTags.length === 0 ? (
                  <p className="text-xs text-gray-400 p-2">Aucun tag sur la sélection</p>
                ) : (
                  <div className="max-h-32 overflow-y-auto">
                    {selectedDocsTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => requestBulk((keys) => handleBulkRemoveTag(keys, tag))}
                        className="block w-full text-left px-2 py-1 text-xs hover:bg-gray-100 rounded"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Extraire — uploaded / error uniquement */}
          <button
            onClick={handleBulkExtract}
            disabled={!hasSelection || bulkActionLoading || extractableDocs.length === 0}
            title={
              !hasSelection
                ? 'Sélectionnez au moins un document'
                : extractableDocs.length === 0
                ? 'Aucun document avec statut uploaded ou error dans la sélection'
                : `${extractableDocs.length} document(s) sera/seront extrait(s)`
            }
            className="px-3 py-1.5 text-xs font-medium rounded bg-purple-100 text-purple-800 hover:bg-purple-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Extraire
          </button>

          {/* Ré-extraire — extracted / validated uniquement (avec modale) */}
          <button
            onClick={() => setShowReextractConfirm(true)}
            disabled={!hasSelection || bulkActionLoading || reextractableDocs.length === 0}
            title={
              !hasSelection
                ? 'Sélectionnez au moins un document'
                : reextractableDocs.length === 0
                ? 'Aucun document avec statut extracted ou validated dans la sélection'
                : `${reextractableDocs.length} document(s) sera/seront ré-extrait(s)`
            }
            className="px-3 py-1.5 text-xs font-medium rounded bg-orange-100 text-orange-800 hover:bg-orange-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Ré-extraire
          </button>

          {/* Supprimer — bloqué si des documents sélectionnés sont en cours d'extraction */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={!hasSelection || bulkActionLoading || documents.some((d) => selectedKeys.has(d.id) && d.status === 'processing')}
            className="px-3 py-1.5 text-xs font-medium rounded bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Supprimer
          </button>

          <span className="text-gray-300">|</span>

          {/* Export CSV */}
          <button
            onClick={() => requestBulk(handleBulkExportCsv)}
            disabled={!hasSelection || isMultiDomain}
            title={isMultiDomain ? 'Export impossible : documents de domaines différents' : ''}
            className="px-3 py-1.5 text-xs font-medium rounded bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Export CSV
          </button>

          {/* Export JSON */}
          <button
            onClick={() => requestBulk(handleBulkExportJson)}
            disabled={!hasSelection || isMultiDomain}
            title={isMultiDomain ? 'Export impossible : documents de domaines différents' : ''}
            className="px-3 py-1.5 text-xs font-medium rounded bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Export JSON
          </button>
        </div>
      </div>

      {/* Reextract confirmation modal */}
      <ConfirmModal
        open={showReextractConfirm}
        title="Confirmer la ré-extraction"
        message={
          <div className="space-y-2">
            <p>
              {reextractableDocs.length} document(s) seront ré-extraits.
              {reextractableDocs.some((d) => d.status === 'validated') && (
                <>
                  {' '}
                  Les validations existantes seront conservées en historique
                  mais devront être refaites sur la nouvelle version.
                </>
              )}
            </p>
            <p className="text-xs text-gray-500">
              La configuration active du tenant sera utilisée (prompt, schéma).
              Pour personnaliser, ré-extrayez les documents un par un depuis leur fiche.
            </p>
          </div>
        }
        confirmLabel={`Ré-extraire ${reextractableDocs.length} document(s)`}
        cancelLabel="Annuler"
        onCancel={() => setShowReextractConfirm(false)}
        onConfirm={handleBulkReextract}
      />

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la suppression</h3>
            <p className="text-sm text-gray-600 mb-4">
              Voulez-vous vraiment supprimer {selectedKeys.size} document(s) ? Cette action est irréversible.
              {hiddenSelectedCount > 0 && (
                <span className="block mt-2 text-orange-700">
                  ⚠️ Dont {hiddenSelectedCount} hors de la vue actuellement affichée.
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {bulkActionLoading ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de portée bulk (sélection hors vue) — export & tags */}
      {bulkScopeModal}

      {loading ? (
        <div className="text-center py-8">
          <svg className="animate-spin h-8 w-8 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-gray-500">Chargement...</p>
        </div>
      ) : (
        <>
          <DocumentList
            ref={tableRef}
            documents={documents}
            onSelect={handleSelectDocument}
            onActionComplete={handleActionComplete}
            selectable
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            keepSelectionOnFilter
            onFilteredCountChange={handleFilteredCountChange}
            extractionPolicy={extractionPolicy}
            currentUserId={user?.id}
            onSortChange={handleServerSortChange}
            controlledSort={controlledSort}
            onFiltersChange={handleFiltersChange}
            controlledFilters={controlledFilters}
            onSearchChange={handleSearchChange}
            controlledSearches={controlledSearches}
            filterFacets={filterFacets}
          />

          {/* Pagination bar (#496) */}
          <div className="mt-3 flex items-center justify-between flex-wrap gap-3 text-sm text-gray-700">
            <div>
              Page {page} / {totalPages} · {totalDocuments} document{totalDocuments > 1 ? 's' : ''} au total
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                « Précédent
              </button>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Suivant »
              </button>
              <label className="ml-2 flex items-center gap-1 text-xs text-gray-500">
                Page size :
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                  className="border border-gray-300 rounded px-1.5 py-1 text-xs bg-white"
                >
                  {PAGE_SIZE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </>
      )}
    </PageContainer>
  );
}
